import { motion } from "framer-motion";
import { Phone, Star, ShieldCheck, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuote } from "@/context/QuoteContext";
import { SITE, IMAGES } from "@/data/site";

export const Hero = () => {
  const { openQuote } = useQuote();

  return (
    <section id="top" data-testid="hero-section" className="relative min-h-[100svh] flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img src={IMAGES.hero} alt="Modern home with premium roofing in California" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/92 via-slate-950/75 to-slate-950/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-slate-950/30" />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-28 pb-16 w-full">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full bg-red-700/15 border border-red-600/30 px-4 py-1.5 mb-6"
          >
            <ShieldCheck className="h-4 w-4 text-red-500" />
            <span className="text-xs sm:text-sm font-bold tracking-wide text-red-400 uppercase">
              Trusted Since 1980, {SITE.years}+ Years Strong
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-display font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-balance"
          >
            California's Most Trusted{" "}
            <span className="text-red-600">Roofing & Construction</span> Company
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-5 text-lg text-slate-200 max-w-xl leading-relaxed"
          >
            From roof repairs to full commercial builds, the owner personally verifies every job.
            Backed by warranties the competition can't match. Serving {SITE.areas}.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-8 flex flex-col sm:flex-row gap-3"
          >
            <Button
              data-testid="hero-quote-btn"
              onClick={() => openQuote()}
              size="lg"
              className="bg-red-700 hover:bg-red-800 text-white font-bold rounded-full text-base px-8 h-14 hover:scale-[1.03] transition-transform shadow-lg shadow-red-900/30"
            >
              Get My Free Quote <ArrowRight className="ml-1 h-5 w-5" />
            </Button>
            <a href={SITE.phoneHref} data-testid="hero-call-btn">
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto border-white/30 bg-white/10 text-white hover:bg-white hover:text-slate-900 font-bold rounded-full text-base px-8 h-14 backdrop-blur-sm"
              >
                <Phone className="mr-1 h-5 w-5" /> Call {SITE.phone}
              </Button>
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-slate-300"
          >
            <div className="flex items-center gap-1.5">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-red-500 text-red-500" />
                ))}
              </div>
              <span className="text-sm font-semibold">Highly Rated by Local Clients</span>
            </div>
            <span className="text-sm font-medium">Residential, HOA, Casino, Commercial</span>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
